import { RcIcon } from '@/components/atoms/RcIcon'
import { RcSuspense } from '@/components/atoms/RcSuspense'
import { RcTrans } from '@/components/atoms/RcTrans'
import {
  ActionMenu,
  ActionMenuOption
} from '@/components/molecules/interactive/ActionMenu'
import { ConfirmingButton } from '@/components/molecules/interactive/ConfirmingButton'
import { ModalTrigger } from '@/components/organisms/modal/ModalTrigger'
import { spinOnHover } from '@/core/animations'
import { useNavigation } from '@/core/hooks/useNavigation'
import { useParsedParam } from '@/core/hooks/useRouteParams'
import { CompetitionRoute, RootRoute } from '@/core/route-keys'
import { useBracketMatchesUber } from '@/entity/bracket-match/useBracketMatchesUber'
import { useCompetitionTeam } from '@/entity/competition-team/useCompetitionTeam'
import { useCompetitionTeamMembers } from '@/entity/competition-team/useCompetitionTeamMembers'
import { useCompetition } from '@/entity/competition/useCompetition'
import { useCompetitionEvents } from '@/entity/competition/useCompetitionEvents'
import { useCompetitionParticipant } from '@/entity/competition/useCompetitionParticipant'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { find, some } from 'lodash-es'
import { TeamJoinButton } from '../shared/TeamJoinButton'
import { TeamSettings } from './TeamSettings'

export const TeamActions = ({ condensed }: { condensed: boolean }) => {
  const { isCompetitionModerator, isSoloCompetition } = useCompetition()
  const { involvement } = useCompetitionParticipant()
  const { team, drop, unDrop, remove } = useCompetitionTeam()
  const { isRegistrationActive } = useCompetitionEvents()
  const { removeMember } = useCompetitionTeamMembers()
  const { navTo } = useNavigation()
  const parsedId = useParsedParam('teamId')
  const { matches } = useBracketMatchesUber({
    request: { entry: [parsedId], size: 1 },
    expand: ''
  })

  const canDrop = !!team?._links?.drop
  const canUnDrop = !!team?._links?.unDrop
  const isTeamAdmin = team?._links?.update
  const participant = find(involvement, it => it.entry?.id === team?.id)
  const isTeamOwner = !!team?._links?._delete || isCompetitionModerator
  const hasMatches = some(matches)

  const showDrop = canDrop && (!isRegistrationActive || hasMatches)
  const showUnDrop = canUnDrop

  const teamCanDelete = isTeamOwner && !showDrop && !showUnDrop && !hasMatches
  const adminCanDelete = !hasMatches && isCompetitionModerator
  const showDelete = teamCanDelete || adminCanDelete
  const options: ActionMenuOption[] = []

  if (isTeamAdmin || isCompetitionModerator) {
    showDrop &&
      options.push({
        key: 'drop',
        icon: <RcIcon icon={['fal', 'arrow-down-to-line']} />,
        display: <RcTrans i18nKey='competition:team.drop-action' />,
        action: async () => drop(),
        confirmation: (
          <RcTrans i18nKey='competition:team.drop-confirmation-message' />
        )
      })

    showUnDrop &&
      options.push({
        key: 'undrop',
        icon: <RcIcon icon={['fal', 'rotate-left']} />,
        display: <RcTrans i18nKey='competition:team.undrop-action' />,
        action: async () => unDrop()
      })

    showDelete &&
      options.push({
        key: 'disband',
        icon: <RcIcon icon={['fal', 'sign-out']} />,
        action: async () => {
          navTo({
            root: RootRoute.Competition,
            rootId: team?.competition?.id,
            subRoute: CompetitionRoute.Info
          })
          await remove(team?.id!)
        },
        display: isSoloCompetition ? (
          <RcTrans i18nKey='competition:team.disband-solo-action' />
        ) : (
          <RcTrans i18nKey='competition:team.disband-action' />
        ),
        confirmation: (
          <RcTrans i18nKey='competition:team.disband-confirmation-message' />
        )
      })
  }

  return (
    <Stack
      direction='row'
      spacing={condensed ? 1 : 2}
      justifyContent={condensed ? 'flex-start' : 'flex-end'}
      alignItems='center'
      sx={{ width: condensed ? '100%' : undefined }}
    >
      {isTeamAdmin ? (
        <ModalTrigger
          activation={handleOpen => (
            <Button
              size={condensed ? 'small' : 'medium'}
              variant='outlined'
              onClick={handleOpen}
              sx={spinOnHover}
            >
              <RcIcon icon={['fal', 'cog']} mr={condensed ? 0 : 1} />
              {condensed ? null : <RcTrans i18nKey='shared.settings' />}
            </Button>
          )}
        >
          {() => <TeamSettings />}
        </ModalTrigger>
      ) : participant ? (
        <Box>
          <ConfirmingButton
            size={condensed ? 'small' : 'medium'}
            message={
              <RcTrans i18nKey='competition:team.quit-confirmation-message' />
            }
            buttonName={<RcTrans i18nKey='shared.leave' />}
            onClick={() => removeMember(participant?.id!)}
          >
            <RcTrans i18nKey='competition:team.quit-action' />
            <RcIcon icon={['fal', 'sign-out']} ml={2} />
          </ConfirmingButton>
        </Box>
      ) : null}

      <RcSuspense skipLoader skipError>
        <TeamJoinButton team={team} condensed={condensed} />
      </RcSuspense>
      {some(options) ? (
        <ActionMenu
          options={options}
          buttonProps={{
            size: condensed ? 'small' : 'medium',
            variant: 'outlined',
            sx: {}
          }}
        />
      ) : null}
    </Stack>
  )
}
